import { ApiError } from '@/lib/api';

type TFunc = (key: string) => string;

/**
 * Map a caught error to a user-facing, translated message — NEVER the raw
 * ``error.message`` (F093/A04, F064). Callers in catch blocks should pass the
 * thrown value straight in:
 *
 * ```ts
 * } catch (e) {
 *   setError(apiErrorMessage(e, t));
 * }
 * ```
 *
 * - ``ApiError`` with ``code: 'network_error'`` → ``common.network_error``
 *   (the offline / unreachable case raised by ``wrapNetworkErrors``).
 * - Anything else (unmapped backend code, plain ``Error``, non-Error throwable)
 *   → ``fallbackKey`` when given, otherwise ``common.error_generic``.
 *
 * The final wording of these keys is owned by F097; this helper only picks the
 * key. It deliberately ignores ``err.message`` so a raw English exception can
 * never reach the owner.
 */
export function apiErrorMessage(err: unknown, t: TFunc, fallbackKey?: string): string {
  if (err instanceof ApiError && err.code === 'network_error') {
    return t('common.network_error');
  }
  return t(fallbackKey ?? 'common.error_generic');
}
